/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { createPortal } from 'react-dom';
import type { RefObject } from 'react';
import type { SearchAutocompleteOption } from './SearchAutocomplete';

export interface SearchAutocompleteDropdownProps {
  options: SearchAutocompleteOption[];
  emptyLabel: string;
  onSelect: (id: string) => void;
  rect: { top: number; left: number; width: number; maxHeight: number };
  portalRef?: RefObject<HTMLDivElement | null>;
}

/**
 * Portal-rendered fixed dropdown list for SearchAutocomplete.
 *
 * Renders via a React portal so it escapes any overflow:hidden/auto ancestor
 * (e.g. a scrollable modal body) and stays correctly positioned.
 *
 * @param props - Filtered options, select handler, and viewport rect.
 * @returns Dropdown panel teleported to document.body.
 */
export function SearchAutocompleteDropdown({
  options,
  emptyLabel,
  onSelect,
  rect,
  portalRef,
}: SearchAutocompleteDropdownProps) {
  const panel = (
    <div
      ref={portalRef}
      data-testid="search-autocomplete-dropdown"
      style={{
        position: 'fixed',
        top: rect.top,
        left: rect.left,
        width: rect.width,
        background: 'var(--surface-primary)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--shadow-lg)',
        maxHeight: rect.maxHeight,
        overflowY: 'auto',
        zIndex: 10001,
        pointerEvents: 'auto',
      }}
    >
      {options.length === 0 ? (
        <div
          data-testid="search-autocomplete-empty"
          style={{
            padding: 12,
            fontSize: 13,
            fontFamily: 'var(--font-family)',
            color: 'var(--text-tertiary)',
            textAlign: 'center',
          }}
        >
          {emptyLabel}
        </div>
      ) : (
        options.map((option) => (
          <div
            key={option.id}
            onMouseDown={() => onSelect(option.id)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '10px 12px',
              cursor: 'pointer',
              borderBottom: '1px solid var(--border-medium)',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.background = 'var(--surface-secondary)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.background = 'transparent';
            }}
          >
            <span
              style={{
                fontSize: 13,
                fontWeight: 500,
                fontFamily: 'var(--font-family)',
                color: 'var(--text-primary)',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              {option.label}
            </span>
            {option.sublabel ? (
              <span
                style={{
                  fontSize: 12,
                  fontFamily: 'var(--font-family)',
                  color: 'var(--text-tertiary)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  minWidth: 0,
                }}
              >
                {option.sublabel}
              </span>
            ) : null}
          </div>
        ))
      )}
    </div>
  );

  return createPortal(panel, document.body);
}
